import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export function useValuations(securityId: string) {
  return useQuery({
    queryKey: ['valuations', securityId],
    queryFn: async () => {
      const { data } = await api.get(`/securities/${securityId}/valuations`);
      return data;
    },
    enabled: !!securityId,
  });
}

export function useAddValuation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      securityId: string;
      amount: number;
      date: string;
      type: string;
      valuer?: string;
      notes?: string;
    }) => {
      const { data } = await api.post(`/securities/${params.securityId}/valuations`, {
        valuationAmount: params.amount,
        valuationDate: params.date,
        valuationType: params.type,
        valuer: params.valuer,
        notes: params.notes,
      });
      return data;
    },
    onSuccess: (_, { securityId }) => {
      qc.invalidateQueries({ queryKey: ['valuations', securityId] });
    },
  });
}

export function useInsurancePolicies(securityId: string) {
  return useQuery({
    queryKey: ['insurance-policies', securityId],
    queryFn: async () => {
      const { data } = await api.get(`/securities/${securityId}/insurance`);
      return data;
    },
    enabled: !!securityId,
  });
}

export function useAddInsurance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      securityId: string;
      number: string;
      insurer: string;
      type: string;
      cover: number;
      premium: number;
      startDate: string;
      endDate: string;
      notes?: string;
    }) => {
      const { data } = await api.post(`/securities/${params.securityId}/insurance`, {
        policyNumber: params.number,
        insurer: params.insurer,
        policyType: params.type,
        coverAmount: params.cover,
        premium: params.premium,
        startDate: params.startDate,
        expiryDate: params.endDate,
        notes: params.notes,
      });
      return data;
    },
    onSuccess: (_, { securityId }) => {
      qc.invalidateQueries({ queryKey: ['insurance-policies', securityId] });
      qc.invalidateQueries({ queryKey: ['expiring-insurance'] });
    },
  });
}

export function useUpdateInsurance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      id: string;
      number?: string;
      insurer?: string;
      type?: string;
      cover?: number;
      premium?: number;
      startDate?: string;
      endDate?: string;
      notes?: string;
    }) => {
      const { data } = await api.patch(`/securities/insurance/${params.id}`, {
        policyNumber: params.number,
        insurer: params.insurer,
        policyType: params.type,
        coverAmount: params.cover,
        premium: params.premium,
        startDate: params.startDate,
        expiryDate: params.endDate,
        notes: params.notes,
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['insurance-policies'] });
      qc.invalidateQueries({ queryKey: ['expiring-insurance'] });
    },
  });
}

export function useExpiringInsurance(days?: number) {
  return useQuery({
    queryKey: ['expiring-insurance', days],
    queryFn: async () => {
      const { data } = await api.get('/securities/expiring-insurance', {
        params: days ? { days } : {},
      });
      return data;
    },
  });
}
